import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { CommonRadioData } from 'src/app/components/common/common-radio/common-radio.component.model';
import { Workflow } from 'src/app/models';
import {
  DataService,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';

@Component({
  selector: 'app-contractor-registration-selector',
  templateUrl: './contractor-registration-selector.component.html',
  styleUrls: ['./contractor-registration-selector.component.scss'],
  standalone: false
})
export class ContractorRegistrationSelectorComponent implements OnInit {
  clientId = '';
  userId = '';

  contractorRegistrationWorkflows: Workflow[] = [];
  selectedWorkflow: Workflow | null = null;

  contractorTypeRadioData: CommonRadioData = {
    options: [],
    value: null,
    direction: 'column'
  };

  isLoading = false;
  isStarting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private context: WorkflowContextService,
    private workflowSvc: WorkflowService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.clientId = this.getClientIdFromRoute();
    this.userId = this.getUserId();

    this.loadContractorRegistrationWorkflows();
  }

  loadContractorRegistrationWorkflows(): void {
    if (!this.clientId) {
      this.contractorRegistrationWorkflows = [];
      this.resetRadioData();
      return;
    }

    this.isLoading = true;

    this.dataService
      .getContractorRegistrationWorkflows(this.clientId, this.userId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: workflows => {
          this.contractorRegistrationWorkflows = workflows || [];

          this.contractorTypeRadioData = {
            options: this.contractorRegistrationWorkflows.map(workflow => ({
              label:
                workflow.version?.name ||
                workflow.name ||
                'Contractor Registration',
              value: workflow.id
            })),
            value: null,
            direction: 'column'
          };
        },
        error: error => {
          console.error(
            'Unable to load contractor registration workflows',
            error
          );

          this.contractorRegistrationWorkflows = [];
          this.resetRadioData();
        }
      });
  }

  onContractorTypeChange(workflowId: string): void {
    this.selectedWorkflow =
      this.contractorRegistrationWorkflows.find(
        workflow => workflow.id === workflowId
      ) || null;

    this.contractorTypeRadioData = {
      ...this.contractorTypeRadioData,
      value: workflowId
    };
  }

  startApplication(): void {
    if (!this.selectedWorkflow?.id || this.isStarting) {
      return;
    }

    this.isStarting = true;

    this.workflowSvc
      .startApplication(this.selectedWorkflow.id, false)
      .pipe(
        finalize(() => {
          this.isStarting = false;
        })
      )
      .subscribe({
        next: result => {
          const applicationId = result?.applicationId;

          if (!applicationId) {
            console.error(
              'Application ID was not returned while starting the contractor registration application.'
            );
            return;
          }

          this.router.navigate(
            ['../workflow-application', applicationId],
            {
              relativeTo: this.route,
              queryParamsHandling: 'merge'
            }
          ).then(() => {
  document.querySelector('.route-body')?.scrollTo({
    top: 0,
    behavior: 'auto'
  })})
        },
        error: error => {
          console.error(
            'Unable to start contractor registration application',
            error
          );
        }
      });
  }

  closeOverlay(): void {
    this.router.navigate(['../'], {
      relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }

  private resetRadioData(): void {
    this.contractorTypeRadioData = {
      options: [],
      value: null,
      direction: 'column'
    };
  }

  private getClientIdFromRoute(): string {
    let currentRoute: ActivatedRoute | null = this.route;

    while (currentRoute) {
      const clientId = currentRoute.snapshot.paramMap.get('clientId');

      if (clientId) {
        return clientId;
      }

      currentRoute = currentRoute.parent;
    }

    return this.context.client?.id || '';
  }

  private getUserId(): string {
    return (
      (this.context as any).user?.id ||
      (this.context as any).userId ||
      (this.context as any).currentUser?.id ||
      ''
    );
  }
}